import { z } from "zod";

/** Every GA4 response carries a literal source tag so the UI can label it. */
const ga4Source = z.literal("ga4");

export const Ga4OverviewSchema = z.object({
  totalUsers: z.number(),
  activeUsers: z.number(),
  newUsers: z.number(),
  returningUsers: z.number(),
  sessions: z.number(),
  engagedSessions: z.number(),
  engagementRate: z.number(),
  avgSessionDurationSec: z.number(),
  avgEngagementTimeSec: z.number().optional(),
  bounceRate: z.number(),
  screenPageViews: z.number(),
  eventCount: z.number(),
  keyEvents: z.number(),
  source: ga4Source,
});

export const Ga4RealtimeSchema = z.object({
  activeUsers: z.number(),
  byCountry: z.array(z.object({ label: z.string(), users: z.number() })),
  source: ga4Source,
});
export type Ga4Realtime = z.infer<typeof Ga4RealtimeSchema>;
export type Ga4Overview = z.infer<typeof Ga4OverviewSchema>;

const Ga4SliceSchema = z.object({
  label: z.string(),
  users: z.number(),
  sessions: z.number(),
  keyEvents: z.number().optional(),
});
export type Ga4Slice = z.infer<typeof Ga4SliceSchema>;

export const Ga4BreakdownSchema = z.object({
  slices: z.array(Ga4SliceSchema),
  source: ga4Source,
});
export type Ga4Breakdown = z.infer<typeof Ga4BreakdownSchema>;

const Ga4EventRowSchema = z.object({ name: z.string(), count: z.number() });

export const Ga4EventsSchema = z.object({
  events: z.array(Ga4EventRowSchema),
  source: ga4Source,
});
export type Ga4Events = z.infer<typeof Ga4EventsSchema>;

export const Ga4KeyEventsSchema = z.object({
  keyEvents: z.array(Ga4EventRowSchema),
  totalKeyEvents: z.number(),
  source: ga4Source,
});
export type Ga4KeyEvents = z.infer<typeof Ga4KeyEventsSchema>;

export const Ga4PagesSchema = z.object({
  pages: z.array(z.object({ path: z.string(), views: z.number(), users: z.number() })),
  source: ga4Source,
});
export type Ga4Pages = z.infer<typeof Ga4PagesSchema>;

export const Ga4TrendSchema = z.object({
  points: z.array(z.object({
    date: z.string(),
    activeUsers: z.number(),
    newUsers: z.number(),
    sessions: z.number(),
  })),
  totalActiveUsers: z.number(),
  peak: z.object({ date: z.string(), activeUsers: z.number() }).nullable(),
  source: ga4Source,
});
export type Ga4Trend = z.infer<typeof Ga4TrendSchema>;

export const Ga4PeakSchema = z.object({
  entries: z.array(z.object({ label: z.string(), users: z.number(), sessions: z.number() })),
  peak: z.object({ label: z.string(), users: z.number() }).nullable(),
  source: ga4Source,
});
export type Ga4Peak = z.infer<typeof Ga4PeakSchema>;
